import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Star } from 'lucide-react';
import { PLATFORM_ICON_SRC } from './PlatformIcon';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { supabase } from '../lib/supabaseClient';

interface AutoReplySettings {
  enabled: boolean;
  min_star_rating: number;
}

interface AutoPostSettings {
  enabled: boolean;
  publish_mode: 'auto' | 'approval';
  frequency_days: number;
  preferred_hour: number;
  guidelines: string;
  cta_type: string;
  cta_url: string;
  next_run_at: string | null;
  last_posted_at: string | null;
}

interface ReviewIgSettings {
  enabled: boolean;
  publish_mode: 'auto' | 'approval';
  last_run_at: string | null;
}

const DEFAULT_REPLY: AutoReplySettings = { enabled: false, min_star_rating: 4 };
const DEFAULT_POST: AutoPostSettings = {
  enabled: false,
  publish_mode: 'approval',
  frequency_days: 7,
  preferred_hour: 10,
  guidelines: '',
  cta_type: '',
  cta_url: '',
  next_run_at: null,
  last_posted_at: null,
};
const DEFAULT_REVIEW_IG: ReviewIgSettings = {
  enabled: false,
  publish_mode: 'approval',
  last_run_at: null,
};

const CTA_OPTIONS = [
  { value: '', label: 'No CTA' },
  { value: 'BOOK', label: 'Book' },
  { value: 'CALL', label: 'Call' },
  { value: 'LEARN_MORE', label: 'Learn more' },
];

function guidelinesOk(text: string) {
  return text.trim().length >= 10;
}

function resolveTimeZone(businessTz?: string | null): string {
  if (businessTz && String(businessTz).trim()) return String(businessTz).trim();
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function formatLocalHourLabel(hour: number, timeZone: string): string {
  const h = Math.min(23, Math.max(0, hour));
  // Anchor on today's date in that timezone, at the chosen hour
  const ymd = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [ys, ms, ds] = ymd.split('-').map(Number);
  let instant = new Date(Date.UTC(ys, ms - 1, ds, h, 0, 0));
  for (let i = 0; i < 4; i++) {
    const shown = Number(
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(instant)
        .find((p) => p.type === 'hour')?.value ?? h,
    );
    const delta = h - shown;
    if (delta === 0) break;
    instant = new Date(instant.getTime() + delta * 60 * 60 * 1000);
  }
  return instant.toLocaleTimeString(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  });
}

function localTimeZoneLabel(timeZone: string): string {
  try {
    const short =
      new Date()
        .toLocaleTimeString(undefined, { timeZone, timeZoneName: 'short' })
        .split(' ')
        .pop() || '';
    return short ? `${timeZone.replace(/_/g, ' ')} (${short})` : timeZone.replace(/_/g, ' ');
  } catch {
    return timeZone;
  }
}

/** Next run ISO using preferred hour in the user's local timezone. */
function computeNextRunIso(frequencyDays: number, preferredHour: number): string {
  const days = Math.min(90, Math.max(7, frequencyDays || 7));
  const next = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  next.setHours(Math.min(23, Math.max(0, preferredHour)), 0, 0, 0);
  if (next.getTime() <= Date.now()) {
    next.setDate(next.getDate() + 1);
    next.setHours(Math.min(23, Math.max(0, preferredHour)), 0, 0, 0);
  }
  return next.toISOString();
}

export default function GoogleBusinessAutomateSettings({
  onBack,
  connectionId: connectionIdProp,
  locationName,
}: {
  onBack: () => void;
  /** Required for per-location settings. Falls back to oldest active location if omitted. */
  connectionId?: string;
  locationName?: string;
}) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = resolveTimeZone(selectedBusiness?.timezone);
  const tzLabel = localTimeZoneLabel(timeZone);

  const [loading, setLoading] = useState(true);
  const [connectionId, setConnectionId] = useState<string | null>(connectionIdProp || null);
  const [savingReply, setSavingReply] = useState(false);
  const [savingPosts, setSavingPosts] = useState(false);
  const [savingReviewIg, setSavingReviewIg] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [instagramConnected, setInstagramConnected] = useState(false);

  const [reply, setReply] = useState<AutoReplySettings>(DEFAULT_REPLY);
  const [posts, setPosts] = useState<AutoPostSettings>(DEFAULT_POST);
  const [reviewIg, setReviewIg] = useState<ReviewIgSettings>(DEFAULT_REVIEW_IG);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      let connId = connectionIdProp || null;
      if (!connId) {
        const { data: conn } = await supabase
          .from('google_business_connections')
          .select('id')
          .eq('business_id', businessId)
          .in('provider', ['ayrshare', 'google'])
          .eq('is_active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        connId = conn?.id || null;
      }
      setConnectionId(connId);

      if (SOCIAL_ENABLED) {
        const { data: igConn } = await supabase
          .from('social_media_connections')
          .select('id')
          .eq('business_id', businessId)
          .eq('platform', 'instagram')
          .eq('provider', 'ayrshare')
          .eq('is_active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        setInstagramConnected(!!igConn?.id);
      } else {
        setInstagramConnected(false);
      }

      if (!connId) {
        setReply(DEFAULT_REPLY);
        setPosts(DEFAULT_POST);
        setReviewIg(DEFAULT_REVIEW_IG);
        return;
      }

      const [{ data: replyRow }, { data: postRow }, { data: reviewIgRow }] = await Promise.all([
        supabase
          .from('google_business_auto_reply_settings')
          .select('enabled, min_star_rating')
          .eq('connection_id', connId)
          .maybeSingle(),
        supabase
          .from('google_business_auto_post_settings')
          .select(
            'enabled, publish_mode, frequency_days, preferred_hour, guidelines, cta_type, cta_url, next_run_at, last_posted_at',
          )
          .eq('connection_id', connId)
          .maybeSingle(),
        supabase
          .from('google_business_review_instagram_settings')
          .select('enabled, publish_mode, last_run_at')
          .eq('connection_id', connId)
          .maybeSingle(),
      ]);

      setReply(
        replyRow
          ? { enabled: !!replyRow.enabled, min_star_rating: replyRow.min_star_rating ?? 4 }
          : DEFAULT_REPLY,
      );

      setPosts(
        postRow
          ? {
              enabled: !!postRow.enabled,
              publish_mode: (postRow.publish_mode as 'auto' | 'approval') || 'approval',
              frequency_days: postRow.frequency_days ?? 7,
              preferred_hour: postRow.preferred_hour ?? 10,
              guidelines: postRow.guidelines || '',
              cta_type: postRow.cta_type || '',
              cta_url: postRow.cta_url || '',
              next_run_at: postRow.next_run_at || null,
              last_posted_at: postRow.last_posted_at || null,
            }
          : DEFAULT_POST,
      );

      setReviewIg(
        reviewIgRow
          ? {
              enabled: !!reviewIgRow.enabled,
              publish_mode: (reviewIgRow.publish_mode as 'auto' | 'approval') || 'approval',
              last_run_at: reviewIgRow.last_run_at || null,
            }
          : DEFAULT_REVIEW_IG,
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to load automation settings');
    } finally {
      setLoading(false);
    }
  }, [businessId, connectionIdProp]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveReply = async () => {
    if (!businessId || !connectionId) {
      toast.error('Connect a Google Business location first');
      return;
    }
    setSavingReply(true);
    try {
      const { error } = await supabase.from('google_business_auto_reply_settings').upsert(
        {
          business_id: businessId,
          connection_id: connectionId,
          enabled: reply.enabled,
          min_star_rating: reply.min_star_rating,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'connection_id' },
      );
      if (error) throw error;
      toast.success('Auto-reply settings saved');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save auto-reply settings');
    } finally {
      setSavingReply(false);
    }
  };

  const savePosts = async () => {
    if (!businessId || !connectionId) {
      toast.error('Connect a Google Business location first');
      return;
    }

    if (posts.enabled && !guidelinesOk(posts.guidelines)) {
      toast.error('Add guidelines (at least 10 characters) before enabling auto posts');
      return;
    }

    if (posts.cta_type && !posts.cta_url.trim()) {
      toast.error('Add a CTA URL or clear the CTA type');
      return;
    }

    const frequency_days = Math.min(90, Math.max(7, Number(posts.frequency_days) || 7));
    const preferred_hour = Math.min(23, Math.max(0, Number(posts.preferred_hour) || 10));

    setSavingPosts(true);
    try {
      const next_run_at = posts.enabled
        ? posts.next_run_at || computeNextRunIso(frequency_days, preferred_hour)
        : null;

      const { error } = await supabase.from('google_business_auto_post_settings').upsert(
        {
          business_id: businessId,
          connection_id: connectionId,
          enabled: posts.enabled && guidelinesOk(posts.guidelines),
          publish_mode: 'auto',
          frequency_days,
          preferred_hour,
          guidelines: posts.guidelines.trim(),
          cta_type: posts.cta_type || null,
          cta_url: posts.cta_url.trim() || null,
          next_run_at,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'connection_id' },
      );
      if (error) throw error;

      const enabled = posts.enabled && guidelinesOk(posts.guidelines);

      setPosts((prev) => ({
        ...prev,
        enabled,
        frequency_days,
        preferred_hour,
        next_run_at,
      }));

      // When enabled, immediately fill the draft queue to 5
      if (enabled) {
        const { data: fillData, error: fillError } = await supabase.functions.invoke(
          'gbp-auto-post-action',
          { body: { action: 'fill_queue', businessId, connectionId } },
        );
        if (fillError || fillData?.error) {
          toast.success('Settings saved — draft queue will fill shortly');
          console.error('fill_queue:', fillError || fillData?.error);
        } else {
          const created = fillData?.created ?? 0;
          const pending = fillData?.pending ?? 0;
          toast.success(
            created > 0
              ? `Settings saved · ${created} draft${created === 1 ? '' : 's'} added (${pending} ready)`
              : `Settings saved · ${pending} draft${pending === 1 ? '' : 's'} ready`,
          );
        }
      } else {
        toast.success('Auto-post settings saved');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save auto-post settings');
    } finally {
      setSavingPosts(false);
    }
  };

  const runPreview = async () => {
    if (!businessId) return;
    if (!guidelinesOk(posts.guidelines)) {
      toast.error('Write guidelines first so the AI knows your brand voice');
      return;
    }
    setPreviewing(true);
    setPreviewText(null);
    try {
      const { data, error } = await supabase.functions.invoke('gbp-auto-post-action', {
        body: {
          action: 'preview',
          businessId,
          connectionId: connectionId || undefined,
          guidelines: posts.guidelines,
          ctaType: posts.cta_type || null,
        },
      });
      if (error || data?.error) {
        toast.error(data?.error || 'Failed to generate preview');
        return;
      }
      setPreviewText(data.content);
      toast.success(`Preview generated (${data.tokensUsed} tokens)`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const saveReviewIg = async () => {
    if (!businessId || !connectionId) {
      toast.error('Connect a Google Business location first');
      return;
    }
    if (reviewIg.enabled && !instagramConnected) {
      toast.error('Connect Instagram before enabling review sharing');
      return;
    }
    setSavingReviewIg(true);
    try {
      const { error } = await supabase.from('google_business_review_instagram_settings').upsert(
        {
          business_id: businessId,
          connection_id: connectionId,
          enabled: reviewIg.enabled && instagramConnected,
          publish_mode: reviewIg.publish_mode,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'connection_id' },
      );
      if (error) throw error;
      setReviewIg((prev) => ({
        ...prev,
        enabled: prev.enabled && instagramConnected,
      }));
      toast.success('Instagram review share settings saved');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save Instagram review share settings');
    } finally {
      setSavingReviewIg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 touch-manipulation"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        ← Back to Automations
      </button>

      <div className="flex items-center gap-3">
        <img
          src={PLATFORM_ICON_SRC.google_business}
          alt=""
          className="w-11 h-11 rounded-xl shadow-sm border border-gray-100 bg-white object-contain"
          draggable={false}
        />
        <div>
          <h2 className="text-base font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
            {locationName || 'Google Business'} automations
          </h2>
          <p className="text-sm text-gray-500">
            {locationName
              ? 'Settings apply only to this location · AI replies and posts use your token balance'
              : 'AI replies and posts use your AI token balance'}
          </p>
        </div>
      </div>

      {/* Auto-reply */}
      <section className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Auto-reply to reviews</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Automatically reply to new Google reviews that meet your rating rule
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-gray-900">Enable auto-reply</p>
          <button
            type="button"
            role="switch"
            aria-checked={reply.enabled}
            onClick={() => setReply((p) => ({ ...p, enabled: !p.enabled }))}
            className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
              reply.enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow mt-1 transition-transform ${
                reply.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className={reply.enabled ? '' : 'opacity-50 pointer-events-none'}>
          <p className="text-sm font-medium text-gray-900 mb-1">Minimum star rating</p>
          <p className="text-xs text-gray-500 mb-3">
            AI replies when a review is this rating or higher
          </p>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5].map((n) => {
              const selected = reply.min_star_rating === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => setReply((p) => ({ ...p, min_star_rating: n }))}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    selected
                      ? 'bg-blue-50 border-blue-600 text-blue-700'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  <Star
                    className={`h-3.5 w-3.5 ${
                      selected ? 'fill-amber-400 text-amber-400' : 'text-gray-400'
                    }`}
                  />
                  {n}+
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => void saveReply()}
          disabled={savingReply}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          style={{ minHeight: '44px' }}
        >
          {savingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save auto-reply
        </button>
      </section>

      {/* Auto posts */}
      <section className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Auto posts</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            AI keeps 5 drafts ready — mixing posts with and without media-library images (each image used once)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Guidelines <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Required. Tone, topics to cover, things to avoid, CTA style, etc.
          </p>
          <textarea
            value={posts.guidelines}
            onChange={(e) => setPosts((p) => ({ ...p, guidelines: e.target.value }))}
            rows={5}
            placeholder="Example: Friendly local tone. Mention ceramic coating and PPF when natural. Never invent discounts. Soft CTA to book online. No emojis."
            className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          {!guidelinesOk(posts.guidelines) && posts.enabled && (
            <p className="text-xs text-red-600 mt-1">Add at least 10 characters to enable auto posts.</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Enable auto posts</p>
            <p className="text-xs text-gray-500">Blocked until guidelines are filled in</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={posts.enabled}
            onClick={() => {
              if (!posts.enabled && !guidelinesOk(posts.guidelines)) {
                toast.error('Write guidelines before enabling');
                return;
              }
              setPosts((p) => ({ ...p, enabled: !p.enabled }));
            }}
            className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
              posts.enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow mt-1 transition-transform ${
                posts.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className={posts.enabled || guidelinesOk(posts.guidelines) ? '' : 'opacity-60'}>
          <p className="text-xs text-gray-500">
            Stages the next 5 posts with dates and publishes each one on its scheduled day. You can edit or remove a staged post any time before then.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Post every (days)
            </label>
            <input
              type="number"
              min={7}
              max={90}
              value={posts.frequency_days}
              onChange={(e) =>
                setPosts((p) => ({
                  ...p,
                  frequency_days: Number(e.target.value),
                }))
              }
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <p className="text-[11px] text-gray-500 mt-1">Min 7 · Max 90</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">
              Preferred hour
            </label>
            <select
              value={posts.preferred_hour}
              onChange={(e) =>
                setPosts((p) => ({ ...p, preferred_hour: Number(e.target.value) }))
              }
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
            >
              {Array.from({ length: 24 }).map((_, h) => (
                <option key={h} value={h}>
                  {formatLocalHourLabel(h, timeZone)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">Your timezone · {tzLabel}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">CTA (optional)</label>
            <select
              value={posts.cta_type}
              onChange={(e) => setPosts((p) => ({ ...p, cta_type: e.target.value }))}
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {CTA_OPTIONS.map((o) => (
                <option key={o.value || 'none'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">CTA URL</label>
            <input
              type="url"
              value={posts.cta_url}
              onChange={(e) => setPosts((p) => ({ ...p, cta_url: e.target.value }))}
              disabled={!posts.cta_type}
              placeholder="https://"
              className="w-full text-sm rounded-xl border border-gray-300 px-3 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
          </div>
        </div>

        {(posts.next_run_at || posts.last_posted_at) && (
          <div className="text-xs text-gray-500 space-y-0.5">
            {posts.last_posted_at && (
              <p>Last posted: {new Date(posts.last_posted_at).toLocaleString()}</p>
            )}
            {posts.next_run_at && posts.enabled && (
              <p>Next run: {new Date(posts.next_run_at).toLocaleString()}</p>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void savePosts()}
            disabled={savingPosts}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            {savingPosts ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save auto posts
          </button>
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={previewing}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50"
            style={{ minHeight: '44px' }}
          >
            {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Generate preview
          </button>
        </div>

        {previewText && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Preview (not published)</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{previewText}</p>
          </div>
        )}

        {posts.enabled && (
          <p className="text-xs text-gray-500">
            AI maintains 5 pending drafts in{' '}
            <span className="font-medium text-gray-700">Google Business → Posts</span>
            {' — the oldest publishes on your schedule.'}
          </p>
        )}
      </section>

      {/* Share new reviews to Instagram */}
      {SOCIAL_ENABLED && (
        <section className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Share new reviews to Instagram</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              When a new 5-star Google review with a written comment arrives, create the same
              Instagram review graphic and caption as manual share.
            </p>
          </div>

          {!instagramConnected ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
              <p className="text-sm text-amber-900">
                Connect Instagram to share Google reviews automatically.
              </p>
              <Link
                to="/app-store?tab=integrations"
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                style={{ minHeight: '44px' }}
              >
                Connect Instagram
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-medium text-gray-900">Enable Instagram share</p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={reviewIg.enabled}
                  onClick={() => setReviewIg((p) => ({ ...p, enabled: !p.enabled }))}
                  className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                    reviewIg.enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow mt-1 transition-transform ${
                      reviewIg.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className={reviewIg.enabled ? '' : 'opacity-50 pointer-events-none'}>
                <p className="text-sm font-medium text-gray-900 mb-2">Publish mode</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(
                    [
                      {
                        id: 'approval' as const,
                        title: 'Schedule it',
                        desc: 'Puts the review card on the Instagram calendar; it publishes on its scheduled day',
                      },
                      {
                        id: 'auto' as const,
                        title: 'Post right away',
                        desc: 'Publishes the review card to Instagram as soon as it is ready',
                      },
                    ]
                  ).map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setReviewIg((p) => ({ ...p, publish_mode: mode.id }))}
                      className={`text-left rounded-xl border px-3 py-3 transition-colors ${
                        reviewIg.publish_mode === mode.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900">{mode.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{mode.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {reviewIg.last_run_at && (
                <p className="text-xs text-gray-500">
                  Last shared: {new Date(reviewIg.last_run_at).toLocaleString()}
                </p>
              )}

              <button
                type="button"
                onClick={() => void saveReviewIg()}
                disabled={savingReviewIg}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                style={{ minHeight: '44px' }}
              >
                {savingReviewIg ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save Instagram share
              </button>
            </>
          )}
        </section>
      )}
    </div>
  );
}
